import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function formatDetail(detail) {
  if (detail == null) return "Error de acceso. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const oauthError = location.state?.error || "";

  useEffect(() => {
    if (user && user.role === "super_ceo_admin") navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role !== "super_ceo_admin") {
        setError("Acceso restringido al Super CEO Admin.");
        return;
      }
      toast.success("Acceso concedido. Bienvenido, Super CEO.");
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0914] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/logo.jpg" alt="ELADPENTESTER" className="w-16 h-16 rounded-2xl object-cover border border-[#581c87] mx-auto mb-4" />
          <h1 className="f-poster text-3xl uppercase text-white" data-testid="login-title">Super CEO Access</h1>
          <p className="f-mono text-[10px] uppercase tracking-widest text-[#a855f7] mt-2">ELADPENTESTER // Command Center</p>
        </div>

        <form onSubmit={submit} className="bg-[#140b22] border border-[#3b126d] rounded-2xl p-8 space-y-4 shadow-xl" data-testid="login-form">
          <div className="flex items-center gap-2 text-[#e879f9] f-mono text-[10px] uppercase tracking-widest mb-2">
            <ShieldAlert className="w-4 h-4" /> Acceso restringido — nivel Super CEO
          </div>
          <div>
            <label htmlFor="admin-email" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">Email</label>
            <input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="ceo@eladpentester.com"
              className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
              data-testid="login-email-input" />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs f-mono uppercase text-[#d8b4fe] mb-1">Contraseña</label>
            <input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className="w-full bg-[#0d0914] border border-[#581c87] rounded px-3.5 py-2.5 text-xs text-white focus:outline-none focus:border-[#e879f9] transition-colors"
              data-testid="login-password-input" />
          </div>
          {error && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <button type="submit" disabled={loading}
            className="w-full bg-[#e879f9] text-[#0d0914] f-poster text-base uppercase tracking-wide py-3 rounded hover:bg-white transition-colors disabled:opacity-50"
            data-testid="login-submit-btn">
            {loading ? "Verificando..." : "Entrar al Dashboard"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-[#3b126d]" />
            <span className="f-mono text-[10px] uppercase tracking-widest text-[#a855f7]">o</span>
            <div className="flex-1 h-px bg-[#3b126d]" />
          </div>

          <button type="button" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + '/admin';
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
            className="w-full bg-white text-[#0d0914] f-poster text-base uppercase tracking-wide py-3 rounded hover:bg-[#e879f9] transition-colors flex items-center justify-center gap-2"
            data-testid="google-login-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continuar con Google
          </button>
          {oauthError && (
            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2" data-testid="oauth-error">
              {oauthError}
            </div>
          )}
          <Link to="/" className="block text-center f-mono text-[10px] uppercase tracking-widest text-[#a855f7] hover:text-[#e879f9] transition-colors" data-testid="back-to-site-link">
            ← Volver a la web
          </Link>
        </form>
      </div>
    </div>
  );
}
